import React from 'react'
import { Link } from '@inertiajs/react'
import { ArrowRight, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  groupHotelCount,
  isDeferredScope,
  leadPath,
  subjectIsPlural,
  type MaybeScope,
} from '@/lib/reporting-scope'

interface DeferredScopeNoteProps {
  scope: MaybeScope
  /**
   * What on THIS page is the unit's rather than this hotel's — "Occupancy and pace", "Targets".
   * A member's page is legitimately a mix, and saying which half is which is the whole job.
   */
  pooled: string
  /** What genuinely belongs to this hotel, so the sentence is not only about what is missing. */
  own?: string
  /** Fixed suffix appended to the lead's backend-served basePath. */
  leadSuffix?: string
  className?: string
}

/**
 * A MEMBER'S PAGE THAT STILL WORKS — and has to say which half of it is whose.
 *
 * A member's calendar is a legitimate page: the occupancy and pace on it are the UNIT's (the engine
 * pools them, so showing this hotel's own curve beside a unit-priced rate is what put an operator
 * on the wrong side of a discount), while the room list and the prices are this hotel's own. That
 * mix is invisible unless it is stated, so this states it — once, inline, above the numbers.
 *
 * Deliberately a note and not a banner: the layout's banner rail means a fault, and combined
 * inventory is not a fault. Renders nothing unless this property is a following listing.
 */
export default function DeferredScopeNote({
  scope,
  pooled,
  own,
  leadSuffix = '',
  className,
}: DeferredScopeNoteProps) {
  if (!isDeferredScope(scope)) return null

  const hotels = groupHotelCount(scope)
  const href = leadPath(scope, leadSuffix)

  return (
    <div
      className={cn(
        'flex flex-wrap items-start gap-x-3 gap-y-1.5 rounded-xl border border-border/60 bg-muted/40 px-4 py-2.5',
        className,
      )}
    >
      <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-foreground">
          {pooled} {subjectIsPlural(pooled) ? 'cover' : 'covers'} all{' '}
          {hotels === 1 ? '1 listing' : `${hotels} listings`} in this combined inventory
          {own ? `: ${own}` : ''}.
        </p>
        {/* The combined inventory is called by its lead listing's name, so the lead is named once. */}
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {/* Unnamed when the person cannot open the lead (change safety R4.3). */}
          Prices are set at the lead listing{scope.leadPropertyName ? `, ${scope.leadPropertyName},` : ''} and bookings
          on every listing count toward the building.
        </p>
      </div>
      {href && (
        <Link
          href={href}
          className="inline-flex shrink-0 items-center gap-1 self-center text-[11px] font-semibold text-primary transition-colors hover:text-primary/80"
        >
          Open {scope.leadPropertyName}
          <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  )
}
